import React, { Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { FiX } from "react-icons/fi";
import {
  StreakStatus,
  useRedeemStreakBonusMutation,
} from "../../../features/streak/streakSlice";
import { StreakRewardHistory } from "./StreakRewardHistory";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

interface StreakModalProps {
  isOpen: boolean;
  onClose: () => void;
  status: StreakStatus;
}

export const StreakModal: React.FC<StreakModalProps> = ({
  isOpen,
  onClose,
  status,
}) => {
  const navigate = useNavigate();
  const [redeemBonus, { isLoading: isRedeeming }] =
    useRedeemStreakBonusMutation();

  const handleRedeem = async () => {
    try {
      const result = await redeemBonus().unwrap();
      toast.success(result.message);
      onClose();
    } catch (error: any) {
      toast.error(error?.data?.message || "Failed to redeem streak bonus");
    }
  };

  const handleTradeNow = () => {
    onClose();
    navigate("/app/giftcard");
  };

  // Render flame progress
  const renderFlameProgress = () => {
    const flames = [];
    for (let i = 0; i < status.streakRequiredDays; i++) {
      const isCompleted = i < status.currentStreak;
      flames.push(
        <div key={i} className="flex flex-col items-center">
          <span
            className={`text-3xl transition-all ${
              isCompleted ? "" : "grayscale opacity-30"
            }`}
          >
            🔥
          </span>
          <span className="text-xs text-gray-400 mt-1">Day {i + 1}</span>
        </div>
      );
    }
    return flames;
  };

  // Only trades of at least minTradeValue count toward the streak.
  const trade =
    status.minTradeValue && status.minTradeValue > 0
      ? `Trade ₦${status.minTradeValue.toLocaleString()} or more`
      : "Trade";

  // Time message
  const getTimeMessage = () => {
    if (status.currentStreak === 0) {
      return `${trade} today to start your streak!`;
    }
    if (status.isSafeToday) {
      return "✅ You've traded today! Keep it up.";
    }
    if (status.hoursRemaining !== null && status.hoursRemaining < 6) {
      return `⚠️ ${trade} in the next ${Math.ceil(status.hoursRemaining)} hours to keep your streak!`;
    }
    return `${trade} within 24 hours to continue your streak.`;
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        {/* Backdrop */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" />
        </Transition.Child>

        {/* Modal */}
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="bg-gray-900 border border-gray-700 rounded-2xl max-w-md w-full p-6 relative">
                {/* Close button */}
                <button
                  onClick={onClose}
                  className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
                >
                  <FiX size={24} />
                </button>

                {/* Redeemable State */}
                {status.redeemAvailable ? (
                  <div className="text-center">
                    <div className="text-6xl mb-4 animate-bounce">🎁</div>

                    <Dialog.Title className="text-2xl font-bold text-orange-400 mb-2">
                      {status.streakRequiredDays}-Day Streak Reward!
                    </Dialog.Title>

                    <p className="text-gray-300 mb-6">
                      You completed a {status.streakRequiredDays}-day trading
                      streak!
                      <br />
                      Claim your reward now.
                    </p>

                    <div className="bg-gradient-to-br from-yellow-900/50 to-orange-900/50 border-2 border-yellow-500 rounded-xl p-6 mb-6">
                      <div className="text-sm text-yellow-400 mb-1">Reward</div>
                      <div className="text-4xl font-bold text-yellow-300">
                        ₦{status.rewardAmount.toLocaleString()}
                      </div>
                      <div className="text-xs text-gray-400 mt-2">
                        Will be credited to your GCBuying wallet
                      </div>
                    </div>

                    <button
                      onClick={handleRedeem}
                      disabled={isRedeeming}
                      className="w-full bg-gradient-to-r from-orange-500 to-red-500 text-white font-bold py-4 px-6 rounded-xl shadow-lg shadow-orange-500/30 disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 transition-opacity"
                    >
                      {isRedeeming ? (
                        <span className="flex items-center justify-center gap-2">
                          <svg
                            className="animate-spin h-5 w-5"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                              fill="none"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            />
                          </svg>
                          Redeeming...
                        </span>
                      ) : (
                        "🔥 REDEEM NOW"
                      )}
                    </button>
                  </div>
                ) : (
                  /* Regular Streak Details */
                  <div>
                    <div className="flex items-center justify-center gap-2 mb-6">
                      <span className="text-3xl">🔥</span>
                      <Dialog.Title className="text-2xl font-bold text-white">
                        Trade Streak
                      </Dialog.Title>
                    </div>

                    {/* Current streak */}
                    <div className="bg-gradient-to-br from-orange-900/30 to-red-900/30 rounded-xl p-4 mb-6 text-center">
                      <div className="text-sm text-orange-400 mb-1">
                        Current Streak
                      </div>
                      <div className="text-4xl font-bold text-orange-300">
                        {status.currentStreak}{" "}
                        {status.currentStreak === 1 ? "day" : "days"}
                      </div>
                    </div>

                    {/* Flame progress */}
                    <div className="flex justify-around mb-6">
                      {renderFlameProgress()}
                    </div>

                    {/* Days left message */}
                    <p className="text-center text-gray-400 text-sm mb-4">
                      {status.streakRequiredDays - status.currentStreak} day
                      {status.streakRequiredDays - status.currentStreak !== 1
                        ? "s"
                        : ""}{" "}
                      left to unlock your ₦
                      {status.rewardAmount.toLocaleString()} bonus!
                    </p>

                    {/* Time message */}
                    <div
                      className={`rounded-lg p-3 mb-6 text-center ${
                        !status.isSafeToday &&
                        status.hoursRemaining !== null &&
                        status.hoursRemaining < 6
                          ? "bg-orange-900/30 border border-orange-500/50"
                          : "bg-green-900/30 border border-green-500/50"
                      }`}
                    >
                      <p className="text-sm text-gray-200">{getTimeMessage()}</p>
                    </div>

                    {/* Trade button */}
                    <button
                      onClick={handleTradeNow}
                      className="w-full bg-primary hover:bg-primary/80 text-black font-bold py-3 px-6 rounded-xl transition-colors"
                    >
                      Trade Now
                    </button>

                    {/* Stats */}
                    {status.totalStreaksCompleted > 0 && (
                      <div className="mt-6 pt-4 border-t border-gray-700">
                        <div className="flex justify-between text-xs text-gray-400">
                          <span>Total streaks completed:</span>
                          <span>{status.totalStreaksCompleted}</span>
                        </div>
                        <div className="flex justify-between text-xs text-gray-400 mt-1">
                          <span>Total earned:</span>
                          <span>
                            ₦{status.totalBonusesEarned.toLocaleString()}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Reward history */}
                    <StreakRewardHistory
                      requiredDays={status.streakRequiredDays}
                    />
                  </div>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default StreakModal;
